import { isPlatformBrowser } from '@angular/common';
import { Component, computed, HostListener, inject, PLATFORM_ID, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ImpersonationStore } from 'shared';

import { AuthStore } from '../../features/authentication/store/auth.store';
import { ThemeService } from '../../services/theme.service';
import { AppBreadcrumbComponent } from '../app-breadcrumb/app-breadcrumb.component';
import { ImpersonationDropdownComponent } from '../impersonation-dropdown/impersonation-dropdown.component';
import { LayoutService } from '../layout.service';

type AppLanguage = 'pt' | 'en';

@Component({
  selector: '[app-topbar]',
  standalone: true,
  imports: [AppBreadcrumbComponent, ImpersonationDropdownComponent, TranslatePipe],
  template: `
    <div class="layout-topbar">
      <div class="topbar-start">
        <button
          type="button"
          class="topbar-menubutton p-link p-trigger"
          (click)="layoutService.onMenuButtonClick()"
          [attr.aria-label]="'layout.topbar.toggleMenu' | translate"
        >
          <i class="pi pi-bars"></i>
        </button>
        <app-breadcrumb />
      </div>
      <div class="topbar-end">
        <ul class="topbar-menu">
          @if (showImpersonationButton()) {
            <li class="relative impersonation-container">
              <button
                type="button"
                (click)="toggleImpersonationDropdown($event)"
                class="p-link layout-topbar-action"
                [attr.aria-label]="'layout.topbar.impersonateUser' | translate"
              >
                <i class="pi pi-users"></i>
              </button>
              @if (isDropdownOpen) {
                <div
                  class="absolute right-0 mt-2 w-80 rounded-lg shadow-lg border z-50"
                  style="background-color: var(--surface-card); border-color: var(--surface-border);"
                >
                  <app-impersonation-dropdown
                    [currentUser]="authStore.user()"
                    (closed)="closeImpersonationDropdown()"
                  />
                </div>
              }
            </li>
          }
          <li>
            <button
              type="button"
              (click)="toggleLanguage()"
              class="p-link layout-topbar-action"
              [attr.aria-label]="'layout.topbar.switchLanguage' | translate"
            >
              {{ currentLang() === 'pt' ? 'EN' : 'PT' }}
            </button>
          </li>
          <li>
            <button
              type="button"
              (click)="toggleDarkMode()"
              class="p-link layout-topbar-action"
              [attr.aria-label]="
                (isDark() ? 'layout.topbar.switchToLightMode' : 'layout.topbar.switchToDarkMode')
                  | translate
              "
            >
              <i class="pi" [class.pi-sun]="isDark()" [class.pi-moon]="!isDark()"></i>
            </button>
          </li>
          <li class="topbar-profile">
            <button
              type="button"
              class="p-link"
              (click)="openProfileSidebar()"
              [attr.aria-label]="'layout.topbar.openProfile' | translate"
            >
              @if (photoUrl()) {
                <img [src]="photoUrl()" [alt]="'layout.topbar.profileAlt' | translate" />
              } @else {
                <div class="topbar-profile-initials">{{ initials() }}</div>
              }
            </button>
          </li>
        </ul>
      </div>
    </div>
  `,
  styles: `
    .layout-topbar-action {
      background: transparent;
      border: none;
      border-radius: 50%;
      width: 2.5rem;
      height: 2.5rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: var(--text-color);
      font-size: 0.875rem;
      font-weight: 600;
      transition: background-color var(--layout-section-transition-duration);
    }

    .layout-topbar-action:hover {
      background-color: var(--primary-color);
      color: var(--primary-color-text);
    }

    .topbar-profile-initials {
      width: 2.5rem;
      height: 2.5rem;
      border-radius: 50%;
      background-color: var(--primary-color);
      color: var(--primary-color-text);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.875rem;
      font-weight: 600;
    }
  `,
})
export class AppTopbarComponent {
  readonly layoutService = inject(LayoutService);
  private readonly themeService = inject(ThemeService);
  protected readonly authStore = inject(AuthStore);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly impersonationStore = inject(ImpersonationStore);
  private readonly translateService = inject(TranslateService);

  readonly isDark = computed(() => this.themeService.theme() === 'dark');
  readonly currentLang = signal<AppLanguage>(this.getStoredLang());
  readonly photoUrl = computed(() => this.authStore.user()?.photoUrl ?? null);

  readonly initials = computed(() => {
    const user = this.authStore.user();
    const firstName = user?.firstName ?? '';
    const lastName = user?.lastName ?? '';
    const firstInitial = firstName.charAt(0).toUpperCase();
    const lastInitial = lastName.charAt(0).toUpperCase();
    return firstInitial + lastInitial || 'U';
  });

  readonly canImpersonate = computed(() => {
    const user = this.authStore.user();
    return user ? this.impersonationStore.canImpersonate(user) : false;
  });

  readonly showImpersonationButton = computed(() => {
    return this.canImpersonate() && !this.impersonationStore.isImpersonating();
  });

  isDropdownOpen = false;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isDropdownOpen) return;
    const target = event.target as HTMLElement;
    const container = target.closest('.impersonation-container');
    if (!container) {
      this.isDropdownOpen = false;
    }
  }

  toggleImpersonationDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  closeImpersonationDropdown(): void {
    this.isDropdownOpen = false;
  }

  toggleDarkMode(): void {
    this.themeService.toggleTheme();
  }

  toggleLanguage(): void {
    const next: AppLanguage = this.currentLang() === 'pt' ? 'en' : 'pt';
    this.currentLang.set(next);
    this.translateService.use(next);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('app_language', next);
    }
  }

  openProfileSidebar(): void {
    this.layoutService.profileSidebarVisible.set(true);
  }

  private getStoredLang(): AppLanguage {
    if (!isPlatformBrowser(this.platformId)) {
      return 'pt';
    }
    const stored = localStorage.getItem('app_language');
    if (stored === 'en' || stored === 'pt') {
      return stored;
    }
    return 'pt';
  }
}
